import type { HexTileMap, HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules';
import {
  computeVision,
  exploredKeys,
  type PlayerId,
  revealedTiles,
  type SimState,
  type TileVisibility,
} from '../sim';
import { rememberVisible, type TileMemory } from './lastSeen';

/**
 * The player's fog of war as one immutable snapshot: a new `Fog` (with the
 * next `version`) only when what they see or have explored changes, so
 * components can depend on it directly.
 */
export interface Fog {
  readonly version: number;
  /** Tiles in sight plus attackers' tiles revealed through fog (sim `revealedTiles`). */
  readonly visible: ReadonlySet<HexTileMapKey>;
  /** Tiles in sight alone: never the revealed attackers' tiles. */
  readonly inSight: ReadonlySet<HexTileMapKey>;
  /** Every tile ever seen this mount, plus the sim's own explored record. */
  readonly explored: ReadonlySet<HexTileMapKey>;
  /**
   * By `visible`: for the units on a tile (an attacker revealed through fog
   * shows, on a tile that otherwise stays fogged).
   */
  readonly visibility: (key: HexTileMapKey) => TileVisibility;
  /**
   * By `inSight`: for the tiles themselves (terrain, clouds, tile memory,
   * borders), which revealed attackers' tiles never open up.
   */
  readonly sight: (key: HexTileMapKey) => TileVisibility;
}

function makeFog(
  version: number,
  visible: ReadonlySet<HexTileMapKey>,
  inSight: ReadonlySet<HexTileMapKey>,
  explored: ReadonlySet<HexTileMapKey>
): Fog {
  return {
    version,
    visible,
    inSight,
    explored,
    visibility: (key) =>
      visible.has(key) ? 'visible' : explored.has(key) ? 'explored' : 'unexplored',
    sight: (key) => (inSight.has(key) ? 'visible' : explored.has(key) ? 'explored' : 'unexplored'),
  };
}

/** How many frames apart vision is recomputed (the first frame always is). */
const VISION_EVERY = 3;

/**
 * Tracks one player's fog across frames: recomputes vision every
 * `VISION_EVERY`th frame, records the tiles in sight in the tile memory
 * (`rememberVisible`, so tiles out of sight read as last seen), and
 * accumulates explored tiles. Persistence resets with the mount (no
 * cross-refresh memory by design).
 */
export class FogTracker {
  /** Each tile as last seen. */
  readonly memory: TileMemory = new Map();
  private frames = 0;
  private fog: Fog = makeFog(0, new Set(), new Set(), new Set());

  /**
   * The fog for the next frame. Vision comes from the `real` state (a spy's
   * owner sees the city it's disguised in); memory and the sim's explored
   * record from the `view` the player is shown.
   */
  update(
    real: SimState,
    view: SimState,
    rules: RuleSet,
    terrain: HexTileMap,
    playerId: PlayerId
  ): Fog {
    const frame = this.frames++;
    if (frame % VISION_EVERY !== 0) return this.fog;

    const sight = computeVision(real, rules, playerId, { reveals: false });
    const next = new Set(sight);
    for (const key of revealedTiles(real, playerId)) next.add(key);
    rememberVisible(this.memory, view, sight);
    const prev = this.fog;
    // The sim's own explored record (every tick, survives snapshot adoption)
    // can hold tiles this throttled pass never saw.
    const added: HexTileMapKey[] = [];
    for (const key of exploredKeys(terrain, view.explored?.[playerId])) {
      if (!prev.explored.has(key)) added.push(key);
    }
    let changed = added.length > 0 || next.size !== prev.visible.size;
    if (!changed) {
      for (const key of next) {
        if (!prev.visible.has(key)) {
          changed = true;
          break;
        }
      }
    }
    if (!changed) return prev;

    const explored = new Set(prev.explored);
    for (const key of added) explored.add(key);
    for (const key of sight) explored.add(key);
    this.fog = makeFog(prev.version + 1, next, sight, explored);
    return this.fog;
  }
}
